import React, { useState, useEffect, useCallback } from 'react';
import { FormFunnelBuilder, SubmissionsList, type FormSubmission, type FormDefinition, formsApi, type FormRecord } from '..';
import { editorApi, type SiteRecord } from '../../editor';
import { Globe, FileInput, Plus, CheckCircle, Sparkles } from 'lucide-react';

const webbuilderApi = { ...editorApi, ...formsApi };

export function FormsPage(): React.JSX.Element {
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [forms, setForms] = useState<FormRecord[]>([]);
  const [activeFormId, setActiveFormId] = useState<string>('');
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFormTitle, setNewFormTitle] = useState('');
  const [newFormAction, setNewFormAction] = useState('coach.crm.lead_capture');

  // Load sites
  useEffect(() => {
    (async () => {
      try {
        const data = await webbuilderApi.listSites();
        if (Array.isArray(data) && data.length > 0) {
          setSites(data);
          setActiveSiteId(data[0].id);
        }
      } catch {
        // Fallback
      }
    })();
  }, []);

  // Load forms when site changes
  const loadForms = useCallback(async (siteId: string) => {
    if (!siteId) return;
    try {
      const data = await webbuilderApi.listForms(siteId);
      if (Array.isArray(data)) {
        setForms(data);
        if (data.length > 0) {
          setActiveFormId(data[0].id);
        } else {
          setActiveFormId('');
          setSubmissions([]);
        }
      }
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    if (activeSiteId) {
      loadForms(activeSiteId);
    }
  }, [activeSiteId, loadForms]);

  // Load submissions when form changes
  const loadSubmissions = useCallback(async (formId: string) => {
    if (!formId) return;
    try {
      const data = await webbuilderApi.listSubmissions(formId);
      if (Array.isArray(data)) {
        setSubmissions(
          data.map((sub) => ({
            id: sub.id,
            formId: sub.form_id,
            idempotencyKey: sub.idempotency_key,
            payload: sub.payload_json || {},
            syncStatus: (sub.sync_status === 'synced' ? 'synced' : 'pending') as 'synced' | 'pending' | 'failed',
            createdAt: sub.created_at ? new Date(sub.created_at).toLocaleString() : '',
          }))
        );
      }
    } catch {
      // Fallback
    }
  }, []);

  useEffect(() => {
    if (activeFormId) {
      loadSubmissions(activeFormId);
    }
  }, [activeFormId, loadSubmissions]);

  const handleCreateForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSiteId || !newFormTitle) return;

    try {
      const form = await webbuilderApi.createForm(activeSiteId, {
        title: newFormTitle,
        target_host_action: newFormAction,
        form_schema_json: {
          fields: [
            { name: 'full_name', label: 'Full Name', type: 'text', required: true },
            { name: 'email', label: 'Email Address', type: 'email', required: true },
            { name: 'phone', label: 'Phone Number', type: 'tel', required: true },
            { name: 'target_exam', label: 'Target Exam (JEE / NEET)', type: 'text', required: false },
          ],
        },
      });
      setForms((prev) => [...prev, form]);
      setActiveFormId(form.id);
      setShowCreateModal(false);
      setNewFormTitle('');
    } catch (err) {
      console.error('Failed to create form', err);
    }
  };

  const activeFormRecord = forms.find((f) => f.id === activeFormId);
  const rawFields = (activeFormRecord?.form_schema_json as any)?.fields;
  const parsedFields = Array.isArray(rawFields) && rawFields.length > 0
    ? rawFields.map((f: any) => ({
        name: String(f.name || ''),
        label: String(f.label || f.name || ''),
        type: (f.type === 'email' || f.type === 'phone' || f.type === 'textarea' || f.type === 'number' || f.type === 'checkbox' ? f.type : 'text') as any,
        required: Boolean(f.required),
      }))
    : [];

  const formDef: FormDefinition = {
    id: activeFormRecord?.id || '',
    name: activeFormRecord?.title || '',
    targetHostAction: activeFormRecord?.target_host_action || 'coach.crm.lead_capture',
    fields: parsedFields,
    spamProtection: 'turnstile',
  };

  const handleSaveFormDef = async (updated: FormDefinition) => {
    if (!activeSiteId || !activeFormId) return;
    try {
      const res = await webbuilderApi.updateForm(activeSiteId, activeFormId, {
        title: updated.name,
        target_host_action: updated.targetHostAction,
        form_schema_json: {
          fields: updated.fields,
          spam_protection: updated.spamProtection,
        },
      });
      setForms((prev) => prev.map((f) => (f.id === res.id ? res : f)));
    } catch (err) {
      console.error('Failed to update form definition', err);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card border border-border p-4 rounded-xl">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Site:</span>
            <select
              value={activeSiteId}
              onChange={(e) => setActiveSiteId(e.target.value)}
              className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
            >
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.subdomain_slug})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <FileInput className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Form:</span>
            {forms.length > 0 ? (
              <select
                value={activeFormId}
                onChange={(e) => setActiveFormId(e.target.value)}
                className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none"
              >
                {forms.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.title} ({f.target_host_action})
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-xs text-muted-foreground">None created yet</span>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 text-xs font-medium transition-opacity min-h-[38px]"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>New Lead Form</span>
        </button>
      </div>

      <FormFunnelBuilder initialForm={formDef} onSave={handleSaveFormDef} />
      <SubmissionsList submissions={submissions} />

      {/* New Form Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full p-6 shadow-2xl flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Create Conversion Form</h2>
            </div>
            <form onSubmit={handleCreateForm} className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Form Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. JEE 2027 Admissions Lead Form"
                  value={newFormTitle}
                  onChange={(e) => setNewFormTitle(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Target Coaching Action</label>
                <select
                  value={newFormAction}
                  onChange={(e) => setNewFormAction(e.target.value)}
                  className="w-full text-sm p-2 rounded-md border border-border bg-background text-foreground"
                >
                  <option value="coach.crm.lead_capture">coach.crm.lead_capture (Direct CRM Sync)</option>
                  <option value="coach.scholarship.register">coach.scholarship.register (Scholarship Exam)</option>
                  <option value="admissions.enquiry">admissions.enquiry (General Enquiry)</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Create Form
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'Forms & Conversion Funnels' };
